import type { IAddon, IData, IDataPoint, IEvent, TDescriptionTable } from 'Components/sq-graphics/interfaces';
import { getLEDDTimeseries } from 'neuro-calculation-commons';
import { exists, isNumber, isPartialDate } from 'neuro-utils';
import { flatten } from 'ramda';
// import { ftmtrsScore, isUpdrsComplete, updrsScoreSum } from 'Routes/Updrs/utils';
// import {
//   calculateAdlIndex,
//   calculateAdlTotal,
//   calculateFBIMODScore,
//   calculateIadlScore,
//   fbiModFields,
// } from 'Routes/DiagnosticSurvey/utils';
import { isEmpty } from 'Utility/ramdaReplacement';
import colors from '../../../../config/theme/colors';
import type { TADLLevel } from 'neuro-schemas/adl';
import { adl } from 'neuro-schemas/adl';
import { formatPartialDate } from 'Utility/date';
import { cdr, cerad, fbimod, ftmtrs, iadl, mmse, moca, updrs_iii } from 'neuro-schemas';
import { filterDocumentsByType, isByInformant } from 'Utility/documentHandling';

const {
  utils: {
    getCDRClass: getCdrClass,
    calculateCDRScore: getCdrTotalScore,
    getFTLDCDRClass: getFtldCdrClass,
    calculateFTLDCDRScore: getFtldCdrTotalScore,
  },
} = cdr;

const { calculateScore: mmseTotalScore } = mmse;

const { calculateScore: mocaTotalScore } = moca;

const {
  utils: { calculateCeradTotal, getCeradTotalCutOffScore, getCeradUpdatedComputedData },
  config: { cutOffScoresByEducationYears, maxScores, taskToTitleMap },
} = cerad;

export const convertGeneTestToTimeline = (
  docs: IDocumentWithTypeV2<'geneTest'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): IAddon[] | undefined => {
  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];
  docs.forEach(doc => {
    if (!isPartialDate(doc.date)) return undefined;
    const events: IEvent[] = [];
    events.push({
      date: dateFromPartialUpdateTimeFrame(doc.date),
      eventType: 'study',
      title: fm('geneTest.shortTitle'),
      description: [
        {
          title: fm('geneTest.geneTestType'),
          values: doc.geneTestType ? fm(`geneTest.opts.${doc.geneTestType}`) : undefined,
          condition: exists(doc.geneTestType),
        },
      ],
    });
    addons.push({
      id: 'geneTest',
      title: fm('geneTest.title'),
      titleDescription: undefined,
      items: [],
      events,
    });

    return;
  });
  return addons.length > 0 ? addons : undefined;
};

export const convertThalamotomyToTimeline = (
  docs: IDocumentWithTypeV2<'thalamotomy'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IAddon> | undefined => {
  if (docs.length === 0) return undefined;
  const addons: IAddon[] = [];
  const events: IEvent[] = [];
  docs.forEach(doc => {
    if (!isPartialDate(doc.date)) return;
    events.push({
      date: dateFromPartialUpdateTimeFrame(doc.date),
      eventType: 'treatment',
      title: fm('thalamotomyHifu.thalamotomy.title'),
      description: [
        {
          title: fm('thalamotomyHifu.thalamotomy.treatmentHospital'),
          values: doc.placeOfOperation ?? undefined,
        },
        {
          title: fm('thalamotomyHifu.thalamotomy.treatmentType'),
          values: doc.typeOfOperation ? fm(`thalamotomyHifu.thalamotomy.opts.${doc.typeOfOperation}`) : undefined,
        },
      ],
    });
    doc.adverseEffects?.forEach(ae => {
      if (!isPartialDate(ae.date)) return;
      events.push({
        date: dateFromPartialUpdateTimeFrame(ae.date),
        eventType: 'adverseEffect',
        title: fm('thalamotomyHifu.adverseEffect'),
        description: [
          {
            title: fm('thalamotomyHifu.adverseEffects'),
            values: (ae.adverseEffects ?? []).map(a => fm(`thalamotomyHifu.opts.${a}`)).join(', '),
          },
          {
            title: fm('thalamotomyHifu.adverseEffectAdditionalInformation'),
            values: ae.additionalInformation,
          },
        ],
      });
    });
  });
  events.length &&
    addons.push({
      id: 'thalamotomy',
      title: fm('thalamotomyHifu.thalamotomy.title'),
      titleDescription: undefined,
      items: [],
      events,
    });
  return addons.length > 0 ? addons : undefined;
};

export const convertHifuToTimeline = (
  docs: IDocumentWithTypeV2<'hifu'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IAddon> | undefined => {
  if (docs.length === 0) return undefined;
  const addons: IAddon[] = [];
  const events: IEvent[] = [];
  docs.forEach(doc => {
    if (!isPartialDate(doc.date)) return;
    events.push({
      date: dateFromPartialUpdateTimeFrame(doc.date),
      eventType: 'treatment',
      title: fm('thalamotomyHifu.hifu.title'),
      description: [
        {
          title: fm('thalamotomyHifu.hifu.treatmentTarget'),
          values: doc.treatmentTarget ? fm(`thalamotomyHifu.hifu.opts.${doc.treatmentTarget}`) : undefined,
        },
        {
          title: fm('thalamotomyHifu.hifu.whatOther'),
          values: doc.treatmentTargetOther,
          condition: doc.treatmentTarget === 'other',
        },
      ],
    });
    doc.adverseEffects?.forEach(ae => {
      if (!isPartialDate(ae.date)) return;
      events.push({
        date: dateFromPartialUpdateTimeFrame(ae.date),
        eventType: 'adverseEffect',
        title: fm('thalamotomyHifu.adverseEffect'),
        description: [
          {
            title: fm('thalamotomyHifu.adverseEffects'),
            values: (ae.adverseEffects ?? []).map(a => fm(`thalamotomyHifu.opts.${a}`)).join(', '),
          },
          {
            title: fm('thalamotomyHifu.adverseEffectAdditionalInformation'),
            values: ae.additionalInformation,
          },
        ],
      });
    });
  });
  events.length &&
    addons.push({
      id: 'hifu',
      title: fm('thalamotomyHifu.hifu.title'),
      titleDescription: undefined,
      items: [],
      events,
    });
  return addons.length > 0 ? addons : undefined;
};

export const convertUpdrsVToTimeline = (
  docs: IDocumentWithTypeV2<'updrs_v'>[],
  fm: (id: string) => string,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IAddon> | undefined => {
  if (docs.length === 0) return undefined;
  const addons: Array<IAddon> = [];
  const events: Array<IEvent> = [];
  docs.forEach(doc => {
    if (!isPartialDate(doc.date)) return;
    isNumber(doc.value) &&
      events.push({
        date: dateFromPartialUpdateTimeFrame(doc.date),
        eventType: 'text',
        title: `${doc.value}`,
        description: { value: `${fm('updrs.opts.v.' + doc.value)}` },
      });
  });
  addons.push({
    id: 'topdata',
    title: 'Hoehn & Yahr',
    titleDescription: undefined,
    items: [],
    events,
  });
  return addons.length > 0 ? addons : undefined;
};

export const convertLeddToGraph = (
  docs: IDocumentWithTypeV2<'medication'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  // LEDD upper boundary values
  const secondaryDataPoints: IDataPoint[] = [];

  const leddDataPoints = getLEDDTimeseries(docs);
  if (!leddDataPoints.length) return undefined;
  leddDataPoints.forEach((med: [PartialDate, number, number, boolean], i) => {
    dataPoints.push({
      date: dateFromPartialUpdateTimeFrame(med[0]),
      value: med[1],
      id: 'ledd',
      title: med[1].toString(),
    });
    let drawLeddMaxPoint = med[1] !== med[2];
    // If ledd and leddMax are both 0, draw leddMax point if there has been some previously
    if (i > 0 && med[1] === 0 && med[2] === 0) {
      for (let j = 0; j < i; j++) {
        if (leddDataPoints[j]?.[1] !== leddDataPoints[j]?.[2]) {
          drawLeddMaxPoint = true;
        }
      }
    }
    (drawLeddMaxPoint || med[3]) &&
      secondaryDataPoints.push({
        date: dateFromPartialUpdateTimeFrame(med[0]),
        value: med[2],
        id: 'leddMax',
        title: med[2].toString(),
      });
  });
  return [
    {
      dataPoints,
      id: 'ledd',
      type: 'lineGraph',
      legend: 'LEDD',
      linegraphProps: {
        stepLine: true,
        doNotMergeOverlappingDataPoints: true,
      },
    },
    {
      dataPoints: secondaryDataPoints,
      id: 'leddMax',
      type: 'lineGraph',
      legend: fm('graph.leddHigh'),
      linegraphProps: {
        stepLine: true,
        doNotMergeOverlappingDataPoints: true,
      },
    },
  ];
};

const { calculateScore: getUPDRSIIIScore } = updrs_iii;

export const convertUpdrsIIIToGraph = (
  docs: IDocumentWithTypeV2<'updrs_iii'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  const secondaryDataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date) || d.type === 'dbs' || d.type === 'levodopa') return;
    const score = getUPDRSIIIScore(d);
    if (isNumber(score)) {
      const value = Number(score);
      if (d.testType === 'UPDRS') {
        dataPoints.push({
          date: dateFromPartialUpdateTimeFrame(d.date),
          value,
          id: 'updrsiii',
          title: value.toString(),
        });
      }
      if (d.testType === 'MDSUPDRS') {
        secondaryDataPoints.push({
          date: dateFromPartialUpdateTimeFrame(d.date),
          value,
          id: 'mdsupdrsiii',
          title: value.toString(),
        });
      }
    }
  });
  return [
    {
      dataPoints,
      id: 'updrsiii',
      type: 'lineGraph',
      legend: 'UPDRS III (0-108)',
    },
    {
      dataPoints: secondaryDataPoints,
      id: 'mdsupdrsiii',
      type: 'lineGraph',
      legend: 'MDS-UPDRS III (0-132)',
      useAdditionalScale: true,
    },
  ];
};

const {
  calculators: { calculateFTMTRSScore: ftmtrsScore },
} = ftmtrs;

export const convertFTMTRSToGraph = (
  docs: IDocumentWithTypeV2<'ftmtrs'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPointsA: IDataPoint[] = [];
  const dataPointsBR: IDataPoint[] = [];
  const dataPointsBL: IDataPoint[] = [];
  const dataPointsC: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const scoreA = ftmtrsScore<'a'>(d, 'a');
    const scoreB = ftmtrsScore<'b'>(d, 'b');
    const scoreC = ftmtrsScore<'c'>(d, 'c');
    if (scoreA !== null) {
      dataPointsA.push({
        date: dateFromPartialUpdateTimeFrame(d.date),
        value: scoreA,
        id: 'ftmtrsA',
        title: scoreA.toString() + ' / 40',
      });
    }
    if (scoreB?.right !== null && scoreB?.right !== undefined && (d.whichHand === 'right' || d.whichHand === 'both')) {
      dataPointsBR.push({
        date: dateFromPartialUpdateTimeFrame(d.date),
        value: scoreB.right,
        id: 'ftmtrsBR',
        title: scoreB.right.toString() + ' / 20',
        description: [
          {
            title: fm('updrs.labels.ftmtrs.dominantHand'),
            values: d.dominantHand && fm('general.' + d.dominantHand),
          },
        ],
      });
    }
    if (scoreB?.left !== null && scoreB?.left !== undefined && (d.whichHand === 'left' || d.whichHand === 'both')) {
      dataPointsBL.push({
        date: dateFromPartialUpdateTimeFrame(d.date),
        value: scoreB.left,
        id: 'ftmtrsBL',
        title: scoreB.left.toString() + ' / 20',
        description: [
          {
            title: fm('updrs.labels.ftmtrs.dominantHand'),
            values: d.dominantHand && fm('general.' + d.dominantHand),
          },
        ],
      });
    }
    if (scoreC !== null) {
      dataPointsC.push({
        date: dateFromPartialUpdateTimeFrame(d.date),
        value: scoreC,
        id: 'ftmtrsC',
        title: scoreC.toString() + ' / 32',
      });
    }
  });
  return [
    {
      dataPoints: dataPointsA,
      id: 'ftmtrsA',
      type: 'lineGraph',
      legend: 'FTM-TRS Part A',
    },
    {
      dataPoints: dataPointsBR,
      id: 'ftmtrsBR',
      type: 'lineGraph',
      legend: 'FTM-TRS Part B ' + fm('general.right'),
    },
    {
      dataPoints: dataPointsBL,
      id: 'ftmtrsBL',
      type: 'lineGraph',
      legend: 'FTM-TRS Part B ' + fm('general.left'),
    },
    {
      dataPoints: dataPointsC,
      id: 'ftmtrsC',
      type: 'lineGraph',
      legend: 'FTM-TRS Part C',
    },
  ];
};

/**
 * Push data to dataPoints corresponding the speciality of the given value
 * @param value value of the datapoint e.g. 12 or 'X'
 * @param dataPoints Array of datapoints in which data is pushed
 * @param data latest data of the documents
 * @param dateFromPartialUpdateTimeFrame function to get js Date from Partial Date
 * @param fm formatting message function
 */
const specialValueChecker = (
  value: string | number | null | undefined,
  dataPoints: Array<IDataPoint>,
  data: { date?: PartialDate | null } & IControlProps,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): void => {
  if (data.date && (value || (typeof value === 'number' && value === 0))) {
    // Here special value is checked, adding more if's allows more special values checking
    if (value === '❌') {
      dataPoints.push({
        date: dateFromPartialUpdateTimeFrame(data.date),
        value: '❌',
        id: 'special',
        title: fm('cognition.unableToPerform'),
      });
    } else {
      dataPoints.push({
        date: dateFromPartialUpdateTimeFrame(data.date),
        value,
        id: 'normal',
        title: value.toString(),
      });
    }
  }
};

export const convertMocaToGraph = (
  docs: IDocumentWithTypeV2<'moca'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const value = d.unableToPerform ? '❌' : mocaTotalScore(d);
    if (!d.unableToPerform && Number.isNaN(Number(value))) return;
    specialValueChecker(value, dataPoints, d, dateFromPartialUpdateTimeFrame, fm);
  });
  return [
    {
      dataPoints,
      id: 'moca',
      type: 'lineGraph',
      legend: 'MOCA',
    },
  ];
};

export const convertMmseToGraph = (
  docs: IDocumentWithTypeV2<'mmse'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const value = d.unableToPerform ? '❌' : mmseTotalScore(d);
    if (!d.unableToPerform && Number.isNaN(Number(value))) return;
    specialValueChecker(value, dataPoints, d, dateFromPartialUpdateTimeFrame, fm);
  });
  return [
    {
      dataPoints,
      id: 'mmse',
      type: 'lineGraph',
      legend: 'MMSE',
    },
  ];
};

export const convertTmtToGraph = (
  docs: Array<IDocumentWithTypeV2<'tmt'>>,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string, values?: { [key: string]: string }) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPointsA: IDataPoint[] = [];
  const dataPointsB: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const valueA =
      (d.tmtaUnableToPerform && !isEmpty(d.tmtaUnableToPerform)) || (d.tmtaTime && d.tmtaTime > 240)
        ? '❌'
        : Number(d.tmtaTime ?? undefined);
    const valueB =
      (d.tmtbUnableToPerform && !isEmpty(d.tmtbUnableToPerform)) || (d.tmtbTime && d.tmtbTime > 240)
        ? '❌'
        : Number(d.tmtbTime ?? undefined);

    if (valueA === '❌' || !Number.isNaN(valueA))
      specialValueChecker(valueA, dataPointsA, d, dateFromPartialUpdateTimeFrame, fm);
    if (valueB === '❌' || !Number.isNaN(valueB))
      specialValueChecker(valueB, dataPointsB, d, dateFromPartialUpdateTimeFrame, fm);
  });
  dataPointsA.forEach(dp => {
    dp.value !== '❌' ? (dp.title = fm('cognition.tmt.tmtSeconds', { seconds: dp.title ?? '-' })) : dp.title;
  });
  dataPointsB.forEach(dp => {
    dp.value !== '❌' ? (dp.title = fm('cognition.tmt.tmtSeconds', { seconds: dp.title ?? '-' })) : dp.title;
  });
  return [
    {
      dataPoints: dataPointsA,
      id: 'tmtA',
      type: 'lineGraph',
      legend: 'TMT-A',
    },
    {
      dataPoints: dataPointsB,
      id: 'tmtB',
      type: 'lineGraph',
      legend: 'TMT-B',
    },
  ];
};

export const convertCdrToGraph = (
  docs: IDocumentWithTypeV2<'cdr'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPointsCdrClass: IDataPoint[] = [];
  const dataPointsFtldCdrClass: IDataPoint[] = [];
  const dataPointsCdrTotalScore: IDataPoint[] = [];
  const dataPointsFtldCdrTotalScore: IDataPoint[] = [];
  docs.forEach(d => {
    const valueCdrClass = getCdrClass(d) ?? undefined;
    const valueFtldCdrClass = getFtldCdrClass(d) ?? undefined;
    const valueCdrTotalScore = getCdrTotalScore(d);
    const valueFtldCdrTotalScore = getFtldCdrTotalScore(d);

    if ((valueCdrClass || valueCdrClass === 0) && d.date) {
      dataPointsCdrClass.push({
        date: dateFromPartialUpdateTimeFrame(d.date),
        value: valueCdrClass.toString(),
        id: 'cdrClass',
        title: `${valueCdrClass} / 3 - ${fm(`cognition.cdr.cdrClassDesc.${valueCdrClass === 0.5 ? '05' : valueCdrClass}`)}`,
      });
    }
    if ((valueFtldCdrClass || valueFtldCdrClass === 0) && d.date) {
      dataPointsFtldCdrClass.push({
        date: dateFromPartialUpdateTimeFrame(d.date),
        value: valueFtldCdrClass.toString(),
        id: 'ftldCdrClass',
        title: `${valueFtldCdrClass} / 3 - ${fm(
          `cognition.cdr.ftldCdrClassDesc.${valueFtldCdrClass === 0.5 ? '05' : valueFtldCdrClass}`,
        )}`,
      });
    }
    if ((valueCdrTotalScore || valueCdrTotalScore === 0) && d.date) {
      dataPointsCdrTotalScore.push({
        date: dateFromPartialUpdateTimeFrame(d.date),
        value: valueCdrTotalScore,
        id: 'cdrTotalScore',
        title: `${valueCdrTotalScore} / 18`,
      });
    }
    if ((valueFtldCdrTotalScore || valueFtldCdrTotalScore === 0) && d.date) {
      dataPointsFtldCdrTotalScore.push({
        date: dateFromPartialUpdateTimeFrame(d.date),
        value: valueFtldCdrTotalScore,
        id: 'ftldCdrTotalScore',
        title: `${valueFtldCdrTotalScore} / 24`,
      });
    }
  });
  return [
    {
      dataPoints: dataPointsCdrClass,
      id: 'cdrClass',
      type: 'lineGraph',
      legend: fm('cognition.cdr.cdrClass'),
    },
    {
      dataPoints: dataPointsFtldCdrClass,
      id: 'ftldCdrClass',
      type: 'lineGraph',
      legend: fm('cognition.cdr.ftldCdrClass'),
    },
    {
      dataPoints: dataPointsCdrTotalScore,
      id: 'cdrTotalScore',
      type: 'lineGraph',
      legend: fm('cognition.cdr.cdrTotalScore'),
      useAdditionalScale: true,
    },
    {
      dataPoints: dataPointsFtldCdrTotalScore,
      id: 'ftldCdrTotalScore',
      type: 'lineGraph',
      legend: fm('cognition.cdr.ftldCdrTotalScore'),
      useAdditionalScale: true,
    },
  ];
};

export const convertDbsEffectToGraph = (
  docs: Array<IDocumentWithTypeV2<'dbsEffect'>>,
  updrsDocs: Array<IDocumentWithTypeV2<'updrs_iii'>>,
  dbsDocs: Array<IDocumentWithTypeV2<'dbs'>>,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPointsUpdrs: IDataPoint[] = [];
  const dataPointsMDS: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    // Assign the updrs test done before dbs treatment to variable called 'before'
    const before = updrsDocs.find(doc => doc._id === d.updrsIIIBeforeID) ?? null;
    // Assign the updrs test done after the dbs treatment to variable called 'after'
    const after = updrsDocs.find(doc => doc._id === d.updrsIIIAfterID) ?? null;

    const dbsOldSettingsId = d.dbsOldSettings ?? '';
    const dbsOldSettings = flatten(dbsDocs.map(doc => doc.settings ?? [])).find(s =>
      (s.programs ? s.programs.map(p => p.id) : []).includes(dbsOldSettingsId),
    );

    const dbsOldProgram = `${
      dbsOldSettings?.programs?.find(p => p.id === dbsOldSettingsId)?.programName
        ? dbsOldSettings.programs.find(p => p.id === dbsOldSettingsId)?.programName
        : fm('graph.programNotChosen')
    } - ${
      dbsOldSettings?.generatorAdjustedDate ? '(' + formatPartialDate(dbsOldSettings.generatorAdjustedDate) + ')' : ''
    }`;

    const dbsNewSettingsId = d.dbsNewSettings ?? '';
    const dbsNewSettings = flatten(dbsDocs.map(d => d.settings ?? [])).find(s =>
      (s.programs ? s.programs.map(p => p.id) : []).includes(dbsOldSettingsId),
    );
    const dbsNewProgram = `${
      dbsNewSettings?.programs?.find(p => p.id === dbsNewSettingsId)?.programName
        ? dbsNewSettings.programs.find(p => p.id === dbsNewSettingsId)?.programName
        : fm('graph.programNotChosen')
    } - ${
      dbsNewSettings?.generatorAdjustedDate ? '(' + formatPartialDate(dbsNewSettings.generatorAdjustedDate) + ')' : ''
    }`;

    const beforeScore = before ? getUPDRSIIIScore(before) : undefined;
    const afterScore = after ? getUPDRSIIIScore(after) : undefined;

    if (isNumber(beforeScore) && isNumber(afterScore)) {
      const beforeValue = Number(beforeScore);
      const afterValue = Number(afterScore);
      if (Number.isNaN(beforeValue) || Number.isNaN(afterValue)) return;
      const change =
        afterValue > beforeValue
          ? Math.round((1 - afterValue / beforeValue) * 100)
          : afterValue < beforeValue
            ? Math.round((1 - afterValue / beforeValue) * 100)
            : 0;
      const title = `${change}%`;
      const description: TDescriptionTable = [
        {
          title: `${fm('updrs.dbsOldSettings')}: `,
          values: dbsOldProgram,
        },
        {
          title:
            d.testType === 'UPDRS'
              ? `${fm('updrs.updrsIIIscoreBeforeDBS')}: `
              : `${fm('updrs.MDSupdrsIIIscoreBeforeDBS')}: `,
          values: beforeValue,
        },
        {
          title: `${fm('updrs.dbsNewSettings')}: `,
          values: dbsNewProgram,
        },
        {
          title:
            d.testType === 'UPDRS'
              ? `${fm('updrs.updrsIIIscoreAfterDBS')}: `
              : `${fm('updrs.MDSupdrsIIIscoreAfterDBS')}: `,
          values: afterValue,
        },
      ];
      if (d.testType === 'UPDRS') {
        dataPointsUpdrs.push({
          date: dateFromPartialUpdateTimeFrame(d.date),
          value: beforeValue,
          additionalValue: afterValue,
          id: 'dbsEffect',
          title,
          description,
        });
      } else if (d.testType === 'MDSUPDRS') {
        dataPointsMDS.push({
          date: dateFromPartialUpdateTimeFrame(d.date),
          value: beforeValue,
          additionalValue: afterValue,
          id: 'dbsEffect',
          title,
          description,
        });
      }
    }
  });
  return [
    {
      dataPoints: dataPointsUpdrs,
      id: 'dbsEffect',
      type: 'boxGraph',
      legend: fm('updrs.dbsTestEffect'),
    },
    {
      dataPoints: dataPointsMDS,
      id: 'dbsEffect',
      type: 'boxGraph',
      legend: fm('updrs.dbsTestEffect'),
      useAdditionalScale: true,
    },
  ];
};

export const convertLevodopaTestToGraph = (
  docs: Array<IDocumentWithTypeV2<'levodopatest'>>,
  updrsDocs: Array<IDocumentWithTypeV2<'updrs_iii'>>,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPointsUpdrs: IDataPoint[] = [];
  const dataPointsMDS: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const before = updrsDocs.find(doc => doc._id === d.updrsIIIBeforeID) ?? null;
    // Assign the updrs test done after the dbs treatment to variable called 'after'
    const after = updrsDocs.find(doc => doc._id === d.updrsIIIAfterID) ?? null;
    const beforeScore = before ? getUPDRSIIIScore(before) : undefined;
    const afterScore = after ? getUPDRSIIIScore(after) : undefined;
    if (isNumber(beforeScore) && isNumber(afterScore)) {
      const beforeValue = Number(beforeScore);
      const afterValue = Number(afterScore);
      if (Number.isNaN(beforeValue) || Number.isNaN(afterValue)) return;
      const change =
        afterValue > beforeValue
          ? Math.round((1 - afterValue / beforeValue) * 100)
          : afterValue < beforeValue
            ? Math.round((1 - afterValue / beforeValue) * 100)
            : 0;
      const title = `${change}%`;
      const description: TDescriptionTable = [
        {
          title:
            d.testType === 'UPDRS' ? `${fm('updrs.updrsIIIscoreBefore')}: ` : `${fm('updrs.MDSupdrsIIIscoreBefore')}: `,
          values: beforeValue,
        },
        {
          title:
            d.testType === 'UPDRS' ? `${fm('updrs.updrsIIIscoreAfter')}: ` : `${fm('updrs.MDSupdrsIIIscoreAfter')}: `,
          values: afterValue,
        },
      ];
      if (d.testType === 'UPDRS') {
        dataPointsUpdrs.push({
          date: dateFromPartialUpdateTimeFrame(d.date),
          value: beforeValue,
          additionalValue: afterValue,
          title,
          id: 'levodopaTest',
          description,
        });
      } else if (d.testType === 'MDSUPDRS') {
        dataPointsMDS.push({
          date: dateFromPartialUpdateTimeFrame(d.date),
          value: beforeValue,
          additionalValue: afterValue,
          title,
          id: 'levodopaTest',
          description,
        });
      }
    }
  });
  return [
    {
      dataPoints: dataPointsUpdrs,
      type: 'boxGraph',
      id: 'levodopaTest',
      legend: fm('updrs.levodopaTestEffect'),
    },
    {
      dataPoints: dataPointsMDS,
      type: 'boxGraph',
      id: 'levodopaTest',
      legend: fm('updrs.levodopaTestEffect'),
      useAdditionalScale: true,
    },
  ];
};

const { calculateScore: calculateFBIMODScore } = fbimod;

const isFbiModScoreField = (key: string): key is keyof IFBIMOD =>
  key !== 'date' && key !== 'respondent' && key !== 'respondentOther' && !key.endsWith('Detail');

export const convertFbiModToGraph = (
  docs: IDocumentWithTypeV2<'fbimod'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
  informants?: Array<IInformant>,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  const informantDataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const value = calculateFBIMODScore(d);
    const description: TDescriptionTable = Object.entries(d)
      .map(([key, value]) => {
        if (isFbiModScoreField(key) && typeof value === 'string' && ['1', '2', '3'].includes(value)) {
          const localizedShortenedKey = fm(`diagnosticSurvey.fbimod.${key}`).split(':')[0];
          return { title: localizedShortenedKey, values: fm(`diagnosticSurvey.fbimod.opts.${value}`) };
        } else return { values: 'filter' };
      })
      .filter(desc => desc.values !== 'filter');
    if (value || value === 0) {
      if (informants && isByInformant(d, informants)) {
        informantDataPoints.push({
          date: dateFromPartialUpdateTimeFrame(d.date),
          value,
          title: `${value} / 66`,
          id: 'informantFbiMod',
          description,
        });
      } else {
        dataPoints.push({
          date: dateFromPartialUpdateTimeFrame(d.date),
          value,
          title: `${value} / 66`,
          id: 'fbiMod',
          description,
        });
      }
    }
  });
  return [
    {
      dataPoints,
      id: 'fbiMod',
      type: 'lineGraph',
      legend: 'FBI-Mod',
    },
    {
      dataPoints: informantDataPoints,
      id: 'informantFbiMod',
      type: 'lineGraph',
      legend: fm('diagnosticSurvey.fbimod.byInformant') + ' FBI-Mod',
    },
  ];
};

const adlLevelArr: Array<TADLLevel> = ['0', '1', '2', '3']; // 'unknown' missing here on purpose

const {
  utils: { calculateADLIndex, calculateADLTotal },
} = adl;

const { calculateScore: calculateIADLScore } = iadl;

export const convertAdlAndIadlToGraph = (
  docs: Array<IDocumentWithTypeV2<'iadl' | 'adl' | 'examination'>>,
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
  informants?: Array<IInformant>,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const adlDocs = filterDocumentsByType(docs, ['adl']);
  const iadlDocs = filterDocumentsByType(docs, ['iadl']);
  const adlDataPoints: IDataPoint[] = [];
  const iadlDataPoints: IDataPoint[] = [];
  const informantIadlDataPoints: IDataPoint[] = [];
  adlDocs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const value = calculateADLIndex(d);
    if (value || value === 0) {
      adlDataPoints.push({
        date: dateFromPartialUpdateTimeFrame(d.date),
        value: value === 'unknown' ? 'N/A' : value,
        title: value === 'unknown' ? 'N/A' : `${fm('diagnosticSurvey.adl.index')}: ${value}`,
        id: 'adl',
        description:
          value === 'unknown'
            ? fm('diagnosticSurvey.adl.notFilledUnknown')
            : adlLevelArr.map(level => {
                const titleSplitted = fm(`diagnosticSurvey.adl.levels.${level}`).split(' (');
                return {
                  title: (
                    <div>
                      <span>{titleSplitted[0]}</span>
                      <br />
                      <span style={{ whiteSpace: 'nowrap' }}>{'(' + titleSplitted[1]}</span>
                    </div>
                  ),
                  values: calculateADLTotal(d, level) ?? '-',
                };
              }),
      });
    }
  });
  iadlDocs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const value = calculateIADLScore(d);
    if (value || value === 0) {
      if (informants && isByInformant(d, informants)) {
        informantIadlDataPoints.push({
          date: dateFromPartialUpdateTimeFrame(d.date),
          value,
          title: `${value}`,
          id: 'informantIadl',
        });
      } else {
        iadlDataPoints.push({
          date: dateFromPartialUpdateTimeFrame(d.date),
          value,
          title: `${value}`,
          id: 'iadl',
        });
      }
    }
  });

  return [
    {
      dataPoints: adlDataPoints,
      id: 'adl',
      type: 'lineGraph',
      legend: 'ADL',
    },
    {
      dataPoints: iadlDataPoints,
      id: 'iadl',
      type: 'lineGraph',
      legend: 'IADL',
      useAdditionalScale: true,
    },
    {
      dataPoints: informantIadlDataPoints,
      id: 'informantIadl',
      type: 'lineGraph',
      legend: fm('diagnosticSurvey.iadl.byInformant') + ' IADL',
      useAdditionalScale: true,
    },
  ];
};

export const convertCeradToGraph = (
  docs: IDocumentWithTypeV2<'cerad'>[],
  backgroundDocs: IDocumentWithTypeV2<'background'>[],
  dateFromPartialUpdateTimeFrame: (date: PartialDate, time?: Time, marker?: true) => Date,
  fm: (id: string) => string,
): Array<IData> | undefined => {
  if (docs.length === 0) return undefined;
  const dataPoints: IDataPoint[] = [];
  docs.forEach(d => {
    if (!isPartialDate(d.date)) return;
    const totalScore = calculateCeradTotal(d);
    if (typeof totalScore !== 'number' || Number.isNaN(totalScore)) return;

    const computed = getCeradUpdatedComputedData(d, backgroundDocs);
    const combined = { ...d, ...computed };

    const educationYears = computed.educationYears;

    const totalCutOffScore = getCeradTotalCutOffScore(educationYears);

    const description: TDescriptionTable = [];

    if (educationYears) {
      description.push(
        {
          title: fm('cognition.cerad.educationYears'),
          values: educationYears,
        },
        { title: fm('cognition.cerad.tasks') },
      );

      const cutOffScores = cutOffScoresByEducationYears[educationYears];
      const tasks = Object.keys(cutOffScores);

      description.push({
        title: (
          <>
            {tasks.map(task => {
              const t = taskToTitleMap[task] ?? task;
              const value = combined[task as keyof typeof combined];

              if (typeof value !== 'number') return;
              return (
                <div
                  key={task}
                  style={{
                    display: 'grid',
                    width: '100%',
                    minHeight: '2.5rem',
                    borderBottom: `1px solid ${colors.lightGray}`,
                    gridTemplateColumns: '1fr 1fr',
                  }}
                >
                  <div style={{ color: colors.tertiaryText }}>{fm(`cognition.cerad.${t}`)}</div>
                  <div style={{ textAlign: 'right' }}>
                    {fm(`cognition.cerad.${value < cutOffScores[task] ? 'ab' : ''}normal`)}
                  </div>
                </div>
              );
            })}
          </>
        ),
      });
    }

    dataPoints.push({
      date: dateFromPartialUpdateTimeFrame(d.date),
      value: totalScore,
      title: `${totalScore} / ${maxScores.total}${
        typeof totalCutOffScore === 'number'
          ? ` (${fm(`cognition.cerad.${totalScore < totalCutOffScore ? 'ab' : ''}normal`)})`
          : ''
      }`,
      id: 'cerad',
      description,
    });
  });
  return [
    {
      dataPoints,
      id: 'cerad',
      type: 'lineGraph',
      legend: 'CERAD',
    },
  ];
};
